// React
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Libs
import { Icon, Popup, Table } from 'semantic-ui-react';

// Components
import LeadsLoadingComponent from './LeadsLoading.component';
import LeadsNothingComponent from './LeadsNothing.component';

// Services
import dateToString from '../../../services/misc/dateToString';

// Styles
import styles from './filter/LeadsFilter.module.css';

// Types
import { LeadsForViewType } from '../../../types/LeadsList';
import { LoaderState } from '../../../types/LoaderState';

type LeadsTableComponentProps = {
    tableConfig: Array<{ key: string; width?: string }>;
    data: LeadsForViewType;
    loaderState: LoaderState;
    filters: { [key: string]: React.ReactElement };
    sorts: { [key: string]: React.ReactElement };
};

/**
 * This component renders daat table with different situtions like:
 * - there are data
 * - there are not data
 * - data are loading
 *
 * @param props LeadsTableComponentProps
 * @returns React.ReactElement
 */
export default function LeadsTableComponent(props: LeadsTableComponentProps): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Picking entry as event delegation operation
    function onPickLead(e: React.MouseEvent<HTMLElement>) {
        const row = (e.target as HTMLElement).closest('tr');

        if (!row) {
            return;
        }

        navigate(`../details/${row.id}`);
    }

    function renderTableBody(loaderState: LoaderState, { list }: LeadsForViewType, cellCount: number) {
        // data are loading
        if (loaderState.state !== 'loaded') {
            return <LeadsLoadingComponent colSpan={cellCount} state={loaderState} />;
        }

        // data are fetched
        if (list.length) {
            return (
                <Table.Body onClick={onPickLead}>
                    <>
                        {list.map((dataItem: { [key: string]: any }, ix: number) => (
                            <Table.Row
                                key={`leads_row_${ix}`}
                                id={dataItem.leadId}
                                data-testid="LeadsListTable.DataRow">
                                {props.tableConfig.map(({ key }, idx) => (
                                    <Table.Cell
                                        key={`leads_cell_value_${idx}`}
                                        warning={dataItem.leadRateLimitExceeded}>
                                        {idx === 0 && dataItem.leadRateLimitExceeded && (
                                            <Popup
                                                content={t(`lead.list.multipleRequests`)}
                                                trigger={<Icon name="warning sign"></Icon>}
                                            />
                                        )}
                                        {key === 'leadCreationTimestamp'
                                            ? dateToString(dataItem[key], 'dateLong')
                                            : dataItem[key]}
                                    </Table.Cell>
                                ))}
                            </Table.Row>
                        ))}
                    </>
                </Table.Body>
            );
        }

        // data do not exist
        else {
            return <LeadsNothingComponent colSpan={cellCount} />;
        }
    }

    return (
        <Table
            celled
            striped
            selectable={!!props.data.list.length}
            size="large"
            style={{ display: 'block', whiteSpace: 'nowrap', overflowX: 'scroll', minHeight: '66vh' }}>
            <Table.Header>
                <Table.Row>
                    {props.tableConfig.map(({ key, width }, ixx) => (
                        <Table.HeaderCell key={`leads_head_${ixx}`}>
                            <div
                                className={props.filters[key] ? styles.tableHeadWithFilter : styles.tableHead}
                                style={{ width: width || undefined }}>
                                <span style={{ padding: '0 1rem 0 0' }}>{t(`lead.list.tableHead.${key}`)}</span>
                                {props.filters && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            margin: '0.2rem 0 0 0',
                                        }}>
                                        {props.filters[key]}
                                    </div>
                                )}
                                {props.sorts && <div>{props.sorts[key]}</div>}
                            </div>
                        </Table.HeaderCell>
                    ))}
                </Table.Row>
            </Table.Header>

            {renderTableBody(props.loaderState, props.data, props.tableConfig.length)}
        </Table>
    );
}
